"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface AnimatedELogoProps {
  width?: number;
  height?: number;
  className?: string;
  strokeWidth?: number;
  autoPlay?: boolean;
  duration?: number;
}

export default function AnimatedELogo({
  width = 181,
  height = 125,
  className = '',
  strokeWidth = 8,
  autoPlay = true,
  duration = 0.5,
}: AnimatedELogoProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;

    // Get the total length of the path
    const pathLength = path.getTotalLength();

    // Set up the stroke-dasharray and stroke-dashoffset
    path.style.strokeDasharray = `${pathLength}`;
    path.style.strokeDashoffset = `${pathLength}`;

    if (autoPlay) {
      // Create the drawing animation
      gsap.fromTo(
        path,
        {
          strokeDashoffset: pathLength,
        },
        {
          strokeDashoffset: 0,
          duration: duration,
        },
      );
    }

    // Hover animation function
    const animateDraw = () => {
      gsap.fromTo(
        path,
        {
          strokeDashoffset: pathLength,
        },
        {
          strokeDashoffset: 0,
          duration: duration,
        },
      );
    };

    // Add hover listeners
    const svg = svgRef.current;
    if (svg && !autoPlay) {
      svg.addEventListener('mouseenter', animateDraw);
      return () => {
        svg.removeEventListener('mouseenter', animateDraw);
      };
    }
  }, [autoPlay, duration]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox="0 0 200 138"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Animated E Logo"
      role="img"
    >
      <path
        ref={pathRef}
        d="M127.823 66.1286C127.823 66.1286 138.928 61.5388 149.44 55.1726C159.952 48.8064 173.51 38.7245 175.053 31.7797C176.597 24.835 174.017 13.2727 144.998 14.4572C115.979 15.6416 19.7331 43.8491 20.9365 73.6503C22.1399 103.451 94.8071 76.1964 94.8071 76.1964C94.8071 76.1964 61.5756 97.6474 56.379 111.304C51.1824 124.96 66.5557 125.337 79.0743 122.258C91.5929 119.178 111.378 109.526 111.378 109.526C111.378 109.526 152.373 90.4179 163.776 86.1127C175.179 81.8075 179.965 82.4086 179.965 82.4086"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
